import { getApiErrorCode, getApiErrorMessage, listedNames } from "../../utils/apiError";
import { bookletLockKind, bookletLockMessage, lockingBooklets } from "../../pages/QuestionPage/review/bookletLock";

// Passage endpoints share some codes with other features whose texts say something else (errors.drafts_not_allowed
// is about folders, errors.unsupported_file_type about .docx or .pdf imports, the folder rights codes about
// questions): those get the passage's own text.
const PASSAGE_TEXTS = {
  drafts_not_allowed: "passages.errors.drafts_not_allowed",
  unsupported_file_type: "passages.errors.docx_only",
  move_not_allowed: "passages.errors.move_not_allowed",
  permission_denied: "passages.errors.permission_denied",
  folder_required: "passages.errors.folder_required",
};

// The names of the tests or booklets an error or a passage's usage lists, counted ("БТС пәндік нұсқасы ×2").
export { listedNames };

// "Q_UPLOAD_1, Q_UPLOAD_2" for the uuids the server refused (`questions` of a 400/403), named from `known` rows
// ({uuid, code, name}); the rest only counted.
const refusedList = (t, uuids, known) => {
  const byUuid = new Map((known || []).map((q) => [q.uuid, q]));
  const named = uuids.map((uuid) => byUuid.get(uuid)).filter(Boolean).map((q) => q.code || q.name);
  const unnamed = uuids.length - named.length;
  return t("passages.errors.refused_questions", {
    list: [...named, ...(unnamed > 0 ? [t("passages.errors.refused_more", { count: unnamed })] : [])].join(", "),
  });
};

// An active BTS booklet refuses with 409 question_in_booklet for one of two reasons, with the same code and booklets,
// told apart by its `lock` (bookletLockKind): "passage", it prints the passage, which then takes no new question
// (set_questions adding one, a question created for the passage); "question", it prints a question the request changes
// (one set_questions would unlink or move, the passage's text or folder). An older server sends no `lock`: there the
// detail of a refused set_questions tells them apart (the backend's PASSAGE_IN_BOOKLET, questions/utils/variants.py),
// and any other refusal counts as the question's.
const PASSAGE_LOCK_DETAIL = "Текст стоит в активном буклете";
const lockOf = (err, linking) =>
  bookletLockKind(err) ??
  (linking && String(err?.response?.data?.detail ?? "").startsWith(PASSAGE_LOCK_DETAIL) ? "passage" : "question");

// What active BTS booklets stop, by `kind`: "question" (the passage and its questions), "passage" (no new question
// joins it) or "relink" (a question set_questions would link, unlink or move), then who returns them to draft. The
// text names the booklets; with `linked` (bookletLock.js linksBooklets: the user gets them as links, shown after it)
// it does not, the links do.
const LOCK_TEXTS = {
  question: "passages.errors.question_in_booklet",
  passage: "passages.errors.passage_in_booklet",
  relink: "passages.errors.relink_in_booklet",
};
export const passageLockMessage = (t, kind, booklets, linked = false) =>
  linked
    ? bookletLockMessage(t, `${LOCK_TEXTS[kind]}_linked`, booklets, true, { counted: true })
    : bookletLockMessage(t, LOCK_TEXTS[kind], booklets);

// Field errors without a code (e.g. "at most 200 questions in a passage") are shown as the server words them.
// `known` (rows {uuid, code, name}) names the questions an error lists. `linked`: the user gets the booklets of a
// question_in_booklet as links after the text (bookletLock.js linksBooklets). `linking`: the request was set_questions.
export const passageErrorMessage = (t, err, fallbackKey, known = [], { linked = false, linking = false } = {}) => {
  const data = err?.response?.data;
  const code = getApiErrorCode(err);
  if (code === "not_found" && Array.isArray(data?.questions)) return t("passages.errors.questions_not_found");
  // An active BTS booklet prints the passage's questions: neither the text nor its questions change until the booklet
  // is back in draft. A question may not join the printed passage, or the request changes a printed question.
  if (code === "question_in_booklet") {
    const kind = lockOf(err, linking) === "passage" ? "passage" : linking ? "relink" : "question";
    return passageLockMessage(t, kind, lockingBooklets(err), linked);
  }
  const refused = Array.isArray(data?.questions) && data.questions.length ? ` ${refusedList(t, data.questions, known)}` : "";
  if (PASSAGE_TEXTS[code]) return `${t(PASSAGE_TEXTS[code])}${refused}`;
  if (!code && data && typeof data === "object" && !data.detail) {
    const message = Object.values(data)
      .flat()
      .find((value) => typeof value === "string" && value.trim());
    if (message) return message;
  }
  return `${getApiErrorMessage(t, err, fallbackKey)}${refused}`;
};
